import { Base } from '../../abstract/base.model';
import { ChemicalLabel } from './chemical-label.model';
import { ChemicalSource } from './chemical-source.model';
import { isRecord } from '../utils/is-record';
import { chemicalLabels } from '../utils/chemical-label-utils';
import { nullableBoolean, nullableString, nullableStringArray } from '../utils/wire-value-utils';

/**
 * A chemical from the system-wide chemical book (`GET /chemicals`, hub `data-models/chemical.ts`).
 * Read-only for web users. EPA rows carry label links and a display name; ADN-recommended rows carry sponsor info.
 * Fields the hub didn't send stay `undefined`; display fallbacks belong in templates.
 */
export class Chemical extends Base {
  name?: string | null;
  epaNumber?: string | null;
  manufacturer?: string | null;
  activeIngredient?: string | null;
  /** CAUTION, WARNING or DANGER. */
  signalWord?: string | null;
  /** herbicide, insecticide, fungicide, fertilizer, adjuvant, igr, miticide, pgr or other. */
  category?: string | null;
  formulation?: string | null;
  /** Restricted Entry Interval. */
  rei?: string | null;
  /** Pre-Harvest Interval. */
  phi?: string | null;
  defaultRate?: string | null;
  targetPests?: string[] | null;
  crops?: string[] | null;
  source?: ChemicalSource | null;

  // ADN-recommended sponsor fields
  sponsorId?: string | null;
  sponsorName?: string | null;
  sponsorLogoUrl?: string | null;
  sponsorBrandColor?: string | null;
  sponsorWebsite?: string | null;
  sponsorPhone?: string | null;
  sponsorEmail?: string | null;
  isFeatured?: boolean | null;
  specialOffer?: string | null;
  sdsUrl?: string | null;
  productUrl?: string | null;

  // EPA fields
  currentLabelUrl?: string | null;
  currentLabels?: ChemicalLabel[] | null;
  /** Primary custom name, else the product name. */
  displayName?: string | null;
  /** Alternate brand names (semicolon-delimited). */
  epaAltBrandNames?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.name = nullableString(source['name']);
    this.epaNumber = nullableString(source['epaNumber']);
    this.manufacturer = nullableString(source['manufacturer']);
    this.activeIngredient = nullableString(source['activeIngredient']);
    this.signalWord = nullableString(source['signalWord']);
    this.category = nullableString(source['category']);
    this.formulation = nullableString(source['formulation']);
    this.rei = nullableString(source['rei']);
    this.phi = nullableString(source['phi']);
    this.defaultRate = nullableString(source['defaultRate']);
    this.targetPests = nullableStringArray(source['targetPests']);
    this.crops = nullableStringArray(source['crops']);
    this.source = nullableString(source['source']) as ChemicalSource | null | undefined;
    this.sponsorId = nullableString(source['sponsorId']);
    this.sponsorName = nullableString(source['sponsorName']);
    this.sponsorLogoUrl = nullableString(source['sponsorLogoUrl']);
    this.sponsorBrandColor = nullableString(source['sponsorBrandColor']);
    this.sponsorWebsite = nullableString(source['sponsorWebsite']);
    this.sponsorPhone = nullableString(source['sponsorPhone']);
    this.sponsorEmail = nullableString(source['sponsorEmail']);
    this.isFeatured = nullableBoolean(source['isFeatured']);
    this.specialOffer = nullableString(source['specialOffer']);
    this.sdsUrl = nullableString(source['sdsUrl']);
    this.productUrl = nullableString(source['productUrl']);
    this.currentLabelUrl = nullableString(source['currentLabelUrl']);
    this.currentLabels = chemicalLabels(source['currentLabels']);
    this.displayName = nullableString(source['displayName']);
    this.epaAltBrandNames = nullableString(source['epaAltBrandNames']);
  }
}
